import {
  abs,
  cos,
  exp,
  float,
  length,
  log,
  max,
  mix,
  mod,
  pow,
  sin,
  smoothstep,
  sqrt,
  uv,
  vec2,
} from 'three/tsl';

import { perlinNoise } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/gradientNoise';
import {
  NEAR_ZERO_3,
  NEAR_ZERO_4,
  TAU,
} from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';

// Ported from "Noise animation - Electric" by nimitz (stormoid.com),
// CC BY-NC-SA 3.0.
//
// THREE SUBSTITUTIONS FROM THE ORIGINAL:
//
//   1. The reference reads noise from a texture (`texture(iChannel0,
//      x*.01).x`). There is no such channel here, so this uses the repo's
//      own gradient noise, with `noiseScale` exposed because the
//      reference's effective frequency came from that texture's own
//      scaling and cannot be matched blind. The ridged construction --
//      abs() of a signed value, so creases form where the noise crosses
//      zero -- is preserved, since that is what gives the filaments hard
//      bright edges instead of soft blobs.
//   2. Octave counts are JS-level unrolled loops, matching every other
//      effect in this folder: they change the shape of the node graph, so
//      they cannot be live uniforms.
//   3. THE RING SCALE IS A CONSTANT IN THIS VARIANT. The reference line is
//      `p /= exp(mod(3.*10., 3.14159))`, and mod(30, PI) has no time in it
//      -- so the rings sit still rather than marching outward. That is
//      reproduced as `ringScale`; `ringPulseSpeed` re-animates it if the
//      outward march is wanted, and defaults to 0 (faithful to this
//      source).

// THE CORE FADE'S OWN GEOMETRY, in uv-distance-from-centre: the pair
// brackets the small disc the log-polar singularity produces.
//
// These were hard constants, on the reasoning that `coreOpacity` was the
// only thing a caller actually wanted to author. That holds right up until
// someone wants the core GONE rather than dimmed -- and how far out the
// singularity reaches depends on `ringScale` and `zoom`, so a fixed radius
// either leaves a bright pip behind or eats into the first real ring.
// They are optional uniforms now, and these are the fallbacks.
//
// Kept small relative to any authored `vignetteRadius` on purpose: this
// must never reach far enough to double as a second vignette.
const DEFAULT_CORE_RADIUS = 0.05;
const DEFAULT_CORE_FEATHER = 0.05;

// Ridged (turbulent) fbm -- the reference's `fbm()`. Five octaves there,
// weight starting at 2 and doubling.
const ridgedFbm = (p: any, noiseScale: any, octaves: number) => {
  let accumulated: any = float(0);
  let weight = 2;
  let sample: any = p.mul(noiseScale);

  for (let i = 0; i < octaves; i++) {
    accumulated = accumulated.add(abs(perlinNoise(sample).mul(2)).div(weight));
    weight *= 2;
    sample = sample.mul(2);
  }

  return accumulated;
};

// The reference's `dualfbm`: two ridged-fbm calls, counter-scrolled, used
// to DISPLACE the domain of a third. Domain displacement rather than plain
// layering is what makes the filaments curl around each other instead of
// sitting in flat parallel bands.
const dualFbm = (
  p: any,
  time: any,
  noiseScale: any,
  octaves: number,
  warpStrength: any,
  swirlSpeed: any,
  extraRotation: any
) => {
  const p2: any = p.mul(0.7);

  const basisX: any = ridgedFbm(p2.sub(time.mul(1.6)), noiseScale, octaves);
  const basisY: any = ridgedFbm(p2.add(time.mul(1.7)), noiseScale, octaves);
  const basis: any = vec2(basisX, basisY).sub(0.5).mul(warpStrength);

  const displaced: any = p.add(basis);

  // The reference's `makem2(time*0.72)` -- the field creeps around as well
  // as flowing. `extraRotation` is added on top: it carries the drag rig's
  // pose and the trail's per-sample offset (see below), so the whole field
  // turns and smears with the pointer.
  const angle: any = time.mul(swirlSpeed).add(extraRotation);
  const c: any = cos(angle);
  const s: any = sin(angle);
  const rotated: any = vec2(
    displaced.x.mul(c).sub(displaced.y.mul(s)),
    displaced.x.mul(s).add(displaced.y.mul(c))
  );

  return ridgedFbm(rotated, noiseScale, octaves);
};

// The reference's `circ()`: rings in LOG-polar space.
//
// Note this is radially symmetric -- it depends only on length(p) -- so
// the rotation applied above does not disturb it. That is a happy
// accident worth keeping: the rings hold still and stay readable while
// the filaments inside them smear with the drag.
const logPolarRings = (p: any, ringFrequency: any) => {
  const r: any = log(max(sqrt(length(p)), float(NEAR_ZERO_4)));

  return abs(mod(r.mul(ringFrequency), TAU).sub(Math.PI))
    .mul(3)
    .add(0.2);
};

interface PowerRingParams {
  time: any;
  zoom: any;
  // The reference folds 0.15 into its own `time` define; exposed here.
  flowSpeed: any;
  noiseScale: any;
  warpStrength: any;
  swirlSpeed: any;
  ringFrequency: any;
  // Static in this variant -- see note 3 at the top.
  ringScale: any;
  // 0 keeps the rings still (faithful); above 0 marches them outward.
  ringPulseSpeed: any;
  ringSharpness: any;
  brightness: any;
  // The reference's `vec3(.321,0.1,0.065)` numerator, divided by the
  // density -- so this is the colour of the DIM filaments, and the bright
  // cores blow out toward white on their own.
  baseColor: any;
  gamma: any;
  // THE CENTRE'S OWN HOTSPOT, faded independently of everything else.
  //
  // `logPolarRings`'s r = log(sqrt(length(ringSpace))) is clamped near
  // zero, but the ring pattern's spatial FREQUENCY still compresses far
  // faster than the display can resolve as the origin is approached --
  // reading as a small, often blown-out disc sitting on top of the rings
  // rather than as one more ring of its own. 1 leaves that disc exactly
  // as authored (today's look, unfaded); 0 removes it, leaving the
  // surrounding rings and filaments untouched.
  coreOpacity: any;
  // The core fade's own geometry, in uv-distance-from-centre. Optional:
  // omitted, both fall back to the constants above.
  coreRadius?: any;
  coreFeather?: any;
  // The reference's radial alpha falloff, which is what keeps this usable
  // as a BACKGROUND: without it the field fills the whole plane and
  // fights the engine for attention.
  vignetteRadius: any;
  vignetteFeather: any;
  opacityScale: any;
  // Accumulated drag pose, in radians, times whatever follow strength the
  // caller wants. Turns the whole field with the pointer.
  dragRotation: any;
  // Radians/second the pointer is currently moving at. Drives the smear.
  trailVelocity: any;
  trailLength: any;
  trailDecay: any;
  octaves: number;
  // Multiplies the ENTIRE field evaluation (three ridged-fbm calls each),
  // so it is by far the most expensive dial here. 1 disables the trail.
  trailSamples: number;
  // A screen-space crop (see home/engine/webgpu/frame-mask), 1 inside the
  // frame and 0 outside it. Folded into `alpha`, which both outputs below
  // already share, so one multiply covers colour and opacity at once.
  // Omitted entirely by every LCV call site.
  mask?: any;
}

export const createPowerRingColorNode = ({
  time,
  zoom,
  flowSpeed,
  noiseScale,
  warpStrength,
  swirlSpeed,
  ringFrequency,
  ringScale,
  ringPulseSpeed,
  ringSharpness,
  brightness,
  baseColor,
  gamma,
  coreOpacity,
  coreRadius,
  coreFeather,
  vignetteRadius,
  vignetteFeather,
  opacityScale,
  dragRotation,
  trailVelocity,
  trailLength,
  trailDecay,
  octaves,
  trailSamples,
  mask,
}: PowerRingParams) => {
  const scaledTime: any = time.mul(flowSpeed);

  // The reference works in a -2..2 box (uv-0.5, then *4). The plane is
  // square, so no aspect correction is needed -- unlike the reference,
  // which is correcting for a viewport.
  const p: any = uv().sub(0.5).mul(4).div(zoom);

  // Ring space. The pulse term is 1 when ringPulseSpeed is 0, leaving the
  // static `ringScale` of the source; above 0 it marches outward, and
  // because log-polar spacing is exponential the mod() wrap lands exactly
  // where it started, so the loop is seamless with no crossfade.
  const pulse: any = exp(mod(scaledTime.mul(ringPulseSpeed), Math.PI));
  const ringSpace: any = p.div(ringScale.mul(pulse));
  const rings: any = pow(
    abs(float(0.1).sub(logPolarRings(ringSpace, ringFrequency))),
    ringSharpness
  );

  // THE TRAIL. The field is evaluated at several rotation offsets running
  // backwards through the last `trailLength` seconds of pointer motion and
  // summed with a decay -- a real angular motion blur of the filaments.
  //
  // Self-limiting: at rest every sample lands on the same angle and the
  // result is identical to no trail (just computed several times over --
  // see trailSamples for that cost).
  let accumulated: any = float(0);
  let weightTotal: any = float(0);

  for (let k = 0; k < trailSamples; k++) {
    const stepFractionJS = trailSamples > 1 ? k / (trailSamples - 1) : 0;
    const rotationBack: any = dragRotation.sub(
      trailVelocity.mul(trailLength).mul(stepFractionJS)
    );

    const field: any = dualFbm(
      p,
      scaledTime,
      noiseScale,
      octaves,
      warpStrength,
      swirlSpeed,
      rotationBack
    );
    const sampleWeight: any = pow(trailDecay, float(k));

    accumulated = accumulated.add(field.mul(sampleWeight));
    weightTotal = weightTotal.add(sampleWeight);
  }

  // Normalised, so changing trailSamples changes the SMOOTHNESS of the
  // smear and not the overall brightness.
  const field: any = accumulated.div(max(weightTotal, float(NEAR_ZERO_4)));

  // The reference's `col = base / rz`: a reciprocal, so the thin creases
  // where the ridged noise approaches zero blow out into bright filaments.
  const density: any = max(field.mul(rings), float(NEAR_ZERO_3));
  const raw: any = baseColor.mul(brightness).div(density);

  // Distance from the plane's own centre, in uv space (0 at the very
  // middle, ~0.707 at a corner) -- shared by the core fade below and the
  // outer vignette further down, so both read the same notion of centre.
  const distanceFromCentre: any = length(uv().sub(0.5));

  // THE CORE FADE. Deliberately small and NOT tied to `vignetteRadius`,
  // which crops the whole field far further out; this only ever touches
  // the tight disc the log-polar singularity itself produces.
  //
  // `coreOpacity` selects between two curves via mix() rather than
  // scaling the fade's strength: at 1 it picks the constant 1 (today's
  // look, unfaded, at every distance); at 0 it picks the smoothstep
  // (faded to 0 at the very centre, ramping back to 1 just past the
  // core's radius). A lerp between "no effect" and "the full effect"
  // like this is what keeps 1 an EXACT no-op rather than an
  // almost-unfaded core.
  const innerRadius: any = coreRadius ?? float(DEFAULT_CORE_RADIUS);
  const outerRadius: any = coreFeather
    ? innerRadius.add(coreFeather)
    : innerRadius.add(float(DEFAULT_CORE_FEATHER));

  const coreFade: any = mix(
    smoothstep(innerRadius, outerRadius, distanceFromCentre),
    float(1),
    coreOpacity
  );
  const shaded: any = pow(abs(raw), gamma).mul(coreFade);

  // Radial falloff in the plane's own uv, so the field fades to nothing
  // before the plane's edge instead of ending on a hard square.
  const vignette: any = float(1).sub(
    smoothstep(
      vignetteRadius,
      vignetteRadius.add(max(vignetteFeather, float(NEAR_ZERO_3))),
      distanceFromCentre
    )
  );

  const alpha: any = mask
    ? vignette.mul(opacityScale).mul(mask)
    : vignette.mul(opacityScale);

  return {
    colorNode: shaded.mul(alpha),
    // Driven by the field's own brightness so the dark gaps between
    // filaments stay transparent rather than washing the background with a
    // flat rectangle of colour.
    opacityNode: max(shaded.r, max(shaded.g, shaded.b)).clamp(0, 1).mul(alpha),
  };
};
